/**
 * API Retry Utility
 * Provides retry logic with exponential backoff for failed requests
 */

/**
 * Default retry configuration
 */
const DEFAULT_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryCondition: (error) => {
    // Retry on:
    // - Network errors (no response)
    // - 5xx server errors
    // - 429 rate limit
    // - Timeout errors
    return (
      !error.response || // Network error (no response from server)
      error.code === 'NETWORK_ERROR' || // Explicit network error
      error.code === 'TIMEOUT' || // Timeout
      error.name === 'AbortError' || // Aborted request
      (error.response && [
        408, // Request Timeout
        429, // Too Many Requests
        500, // Internal Server Error
        502, // Bad Gateway
        503, // Service Unavailable
        504  // Gateway Timeout
      ].includes(error.response.status))
    );
  }
};

/**
 * Sleep for a given number of milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate delay with exponential backoff and jitter
 * @param {number} attempt - Current attempt number (1-based)
 * @param {number} initialDelay - Initial delay in ms
 * @param {number} maxDelay - Maximum delay in ms
 * @param {number} backoffMultiplier - Multiplier for each attempt
 * @returns {number} - Delay in milliseconds
 */
const calculateDelay = (attempt, initialDelay, maxDelay, backoffMultiplier) => {
  // Exponential backoff: initialDelay * (multiplier ^ (attempt - 1))
  const exponentialDelay = initialDelay * Math.pow(backoffMultiplier, attempt - 1);

  // Add jitter (random 0-100ms) to prevent thundering herd
  const jitter = Math.random() * 100;

  // Cap at maxDelay
  return Math.min(exponentialDelay + jitter, maxDelay);
};

/**
 * Execute a function with retry logic
 * @param {Function} apiCall - Async function to execute
 * @param {Object} config - Retry configuration
 * @returns {Promise} - Result of the apiCall
 * @throws {Error} - Last error if all retries exhausted
 */
export const withRetry = async (apiCall, config = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError;

  for (let attempt = 1; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      console.log(`🔄 Attempt ${attempt}/${finalConfig.maxRetries}`);
      return await apiCall();
    } catch (error) {
      lastError = error;
      console.error(`❌ Attempt ${attempt} failed:`, error.message);

      // Don't retry on last attempt
      if (attempt === finalConfig.maxRetries) {
        console.error(`💥 All ${finalConfig.maxRetries} attempts exhausted`);
        break;
      }

      // Check if we should retry
      if (!finalConfig.retryCondition(error)) {
        console.log('⏭️ Retry condition not met, failing fast');
        break;
      }

      // Calculate and wait before retrying
      const delay = calculateDelay(
        attempt,
        finalConfig.initialDelay,
        finalConfig.maxDelay,
        finalConfig.backoffMultiplier
      );

      console.log(`⏳ Waiting ${delay}ms before retry...`);
      await sleep(delay);
    }
  }

  throw lastError;
};

/**
 * Supabase-specific retry wrapper
 * @param {Function} supabaseCall - Supabase client method
 * @param {Object} options - Additional options
 * @returns {Promise} - Result of the supabaseCall
 */
export const supabaseWithRetry = (supabaseCall, options = {}) => {
  return withRetry(supabaseCall, {
    ...options,
    retryCondition: (error) => {
      console.log('🔍 Supabase error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        details: error.details
      });

      // Supabase-specific retry conditions
      const isNetworkError = !error.response;
      const isTimeout = error.code === 'TIMEOUT' || error.message.includes('timeout');
      const isAborted = error.name === 'AbortError';
      const isRateLimit = error.code === 'PGRST301' || error.response?.status === 429;
      const isServerError = error.response?.status >= 500;

      // Retry on network issues, rate limits, and server errors
      const shouldRetry = isNetworkError || isTimeout || isAborted || isRateLimit || isServerError;

      if (shouldRetry) {
        console.log('✅ Will retry due to:', {
          isNetworkError,
          isTimeout,
          isAborted,
          isRateLimit,
          isServerError
        });
      }

      return shouldRetry || options.retryCondition?.(error) || false;
    }
  });
};

/**
 * Create a wrapped version of a function with retry logic
 * @param {Function} fn - Function to wrap
 * @param {Object} config - Retry configuration
 * @returns {Function} - Wrapped function with retry
 */
export const createRetryable = (fn, config = {}) => {
  return async (...args) => {
    return withRetry(() => fn(...args), config);
  };
};

/**
 * Retry a promise with a progress callback
 * @param {Function} apiCall - Async function to execute
 * @param {Function} onProgress - Callback for retry attempts
 * @param {Object} config - Retry configuration
 * @returns {Promise} - Result of the apiCall
 */
export const withRetryAndProgress = async (apiCall, onProgress, config = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError;

  for (let attempt = 1; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      onProgress?.({ attempt, total: finalConfig.maxRetries, status: 'running' });
      const result = await apiCall();
      onProgress?.({ attempt, total: finalConfig.maxRetries, status: 'success' });
      return result;
    } catch (error) {
      lastError = error;
      onProgress?.({ attempt, total: finalConfig.maxRetries, status: 'error', error });

      if (attempt === finalConfig.maxRetries) {
        break;
      }

      if (!finalConfig.retryCondition(error)) {
        break;
      }

      const delay = calculateDelay(
        attempt,
        finalConfig.initialDelay,
        finalConfig.maxDelay,
        finalConfig.backoffMultiplier
      );

      await sleep(delay);
    }
  }

  onProgress?.({
    attempt: finalConfig.maxRetries,
    total: finalConfig.maxRetries,
    status: 'failed',
    error: lastError
  });

  throw lastError;
};

/**
 * Utility to check if an error is retryable
 * @param {Error} error - Error to check
 * @returns {boolean} - True if error is retryable
 */
export const isRetryableError = (error) => {
  return DEFAULT_CONFIG.retryCondition(error);
};

/**
 * Get a user-friendly error message for display
 * @param {Error} error - Error to get message from
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error) => {
  if (!error) return 'Bilinmeyen bir hata oluştu';

  const message = error.message || error.toString();

  // Supabase-specific messages
  if (message.includes('Failed to fetch') || message.includes('Network request failed')) {
    return 'İnternet bağlantınızı kontrol edin';
  }

  if (error.response?.status === 401) {
    return 'Oturumunuzun süresi dolmuş. Lütfen tekrar giriş yapın';
  }

  if (error.response?.status === 403) {
    return 'Bu işlemi gerçekleştirme yetkiniz yok';
  }

  if (error.response?.status === 404) {
    return 'İstenen kaynak bulunamadı';
  }

  if (error.response?.status >= 500) {
    return 'Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin';
  }

  if (error.response?.status === 429) {
    return 'Çok fazla istek gönderdiniz. Lütfen biraz bekleyin';
  }

  // Timeout
  if (error.code === 'TIMEOUT' || message.includes('timeout')) {
    return 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin';
  }

  // Aborted
  if (error.name === 'AbortError') {
    return 'İstek iptal edildi';
  }

  return message;
};

const retryApi = {
  withRetry,
  supabaseWithRetry,
  createRetryable,
  withRetryAndProgress,
  isRetryableError,
  getUserFriendlyErrorMessage
};

export default retryApi;
